import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Typography, Avatar
} from '@mui/material';
import Button from '@mui/material/Button';
import MDButton from 'components/MDButton';
import * as yup from 'yup';
import Swal from 'sweetalert2';
import api from 'core/interceptors/api';
import { useFormik } from 'formik';

const validationAppCategorySchema = yup.object().shape({
  app_category: yup.string()
    .required('App Category is required')
    .matches(/^[A-Za-z\s]+$/, 'App Category can only contain letters and spaces'),
});

export default function AppCategoryAddModel({ open, handleClose }) {
  const [duplicateError, setDuplicateError] = useState('');
  const [image, setImage] = useState(null);
  const [imageError, setImageError] = useState('');
  const [imagePreview, setImagePreview] = useState(null);

  const checkDuplicate = async (value) => {
    const trimmed = value.trim();
    if (!trimmed || !/^[A-Za-z\s]+$/.test(trimmed)) {
      setDuplicateError('');
      return;
    }
    try {
      const response = await api.post('/app_management/app_category/check_duplicate', {
        app_category: trimmed,
      });
      setDuplicateError(response.data.status === 1 ? 'App Category already exists' : '');
    } catch (error) {
      setDuplicateError('Error checking duplicate');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setImageError('Image size must be less than 2MB');
        setImage(null);
        setImagePreview(null);
      } else {
        setImage(file);
        setImageError('');
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setImage(null);
      setImageError('');
      setImagePreview(null);
    }
  };

  const formik = useFormik({
    initialValues: {
      app_category: '',
    },
    validationSchema: validationAppCategorySchema,
    onSubmit: async (values, { resetForm }) => {
      if (duplicateError || imageError || formik.errors.app_category) {
        return;
      }

      try {
        const formData = new FormData();
        formData.append('app_category', values.app_category.trim());
        if (image) {
          formData.append('image', image);
        }

        const response = await api.post('/app_management/app_category/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (response.data.status === 1) {
          Swal.fire({
            icon: 'success',
            title: 'App Category Added Successfully!',
            text: response.data.msg,
            timer: 3000,
            showConfirmButton: false,
          });

          resetForm();
          setImage(null);
          setImageError('');
          setImagePreview(null);
          setDuplicateError('');
          handleClose();
        } else {
          Swal.fire({ icon: 'error', title: 'Error', text: response.data.msg });
        }
      } catch (error) {
        Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to upload app category.' });
      }
    },
  });

  const handleCancel = () => {
    formik.resetForm();
    formik.setErrors({});
    formik.setTouched({});
    setDuplicateError('');
    setImage(null);
    setImageError('');
    setImagePreview(null);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} fullWidth maxWidth="sm">
      <DialogTitle>Add App Category</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="App Category"
                name="app_category"
                value={formik.values.app_category}
                onChange={async (e) => { // Made onChange async
                  formik.handleChange(e);
                  // Manually set field as touched immediately for real-time validation display
                  formik.setFieldTouched('app_category', true, false); 
                  await formik.validateField('app_category'); // Validate immediately
                  
                  // Only run duplicate check if current input passes yup schema
                  if (validationAppCategorySchema.fields.app_category.isValidSync(e.target.value)) {
                    checkDuplicate(e.target.value);
                  } else {
                    setDuplicateError(''); // Clear duplicate error if format is invalid
                  }
                }}
                onBlur={formik.handleBlur} // Keep onBlur for robustness if user skips typing
                fullWidth
                margin="normal"
                error={
                  (formik.touched.app_category && Boolean(formik.errors.app_category)) ||
                  Boolean(duplicateError)
                }
                helperText={
                  (formik.touched.app_category && formik.errors.app_category) ||
                  duplicateError
                }
              />
            </Grid>

            <Grid item xs={12}>
              <MDButton
                variant="outlined"
                color="info"
                component="label"
                fullWidth
                sx={{ justifyContent: 'center' }}
              >
                Upload Image (Max 2MB)
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageChange}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                />
              </MDButton>

              {imagePreview && (
                <Avatar
                  src={imagePreview}
                  variant="rounded"
                  sx={{ mt: 2, width: 100, height: 100, mx: 'auto' }}
                />
              )}

              {imageError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {imageError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <MDButton
            type="submit"
            color="success"
            disabled={!formik.isValid || !formik.dirty || Boolean(duplicateError) || Boolean(imageError)}
          >
            Save
          </MDButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}